/**
 * GENERATED FILE - DO NOT EDIT.
 *
 * Copied from couponbase-builder/packages/template-renderer/src by
 * `pnpm sync:renderer`. Edit it there and re-run the sync; a drift test fails
 * when this copy and the source disagree.
 */

import type { CSSProperties } from "react";
import type { ComponentStyles, GridSize, RenderContext } from "./types";

export function getNestedValue(source: unknown, path?: string): unknown {
    if (path === "") return source;
    if (!path || source == null) return undefined;
    return path
        .split(".")
        .reduce<unknown>(
            (current, key) => (current == null ? undefined : (current as Record<string, unknown>)[key]),
            source,
        );
}

export function hasRenderableValue(value: unknown): boolean {
    if (!value) return false;
    if (typeof value === "string") return value.trim().length > 0;
    return !Array.isArray(value) || value.length > 0;
}

export function resolveBoundValue(
    node: { data?: unknown; placeholder?: string },
    data: unknown,
    staticPageValue?: (placeholder: string) => unknown,
): unknown {
    // `""` means "bind to the item itself" (see getNestedValue); only a truly
    // absent placeholder skips the lookup.
    const dynamicValue =
        node.placeholder !== undefined
            ? (getNestedValue(data, node.placeholder) ?? staticPageValue?.(node.placeholder))
            : undefined;
    return typeof dynamicValue === "string" ? dynamicValue.trim() || node.data : (dynamicValue ?? node.data);
}

export function resolveTextRules(
    rules: Array<{ when: string; equals?: string; text: string; styles?: ComponentStyles }> | undefined,
    data: unknown,
): { text: string; styles?: ComponentStyles } | undefined {
    if (!rules || !rules.length) return undefined;
    for (const rule of rules) {
        const value = getNestedValue(data, rule.when);
        if (value == null || value === "") continue;
        if (rule.equals !== undefined) {
            if (String(value).trim() !== rule.equals) continue;
            return { text: rule.text, styles: rule.styles };
        }

        return { text: rule.text || String(value).trim(), styles: rule.styles };
    }
    return undefined;
}

/** Render a resolved value as text without leaking "[object Object]". */
export function toText(value: unknown): string {
    if (value == null) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    return "";
}

function isRenderableHref(value: unknown): value is string {
    return typeof value === "string" && value.trim().length > 0 && value.trim() !== "#";
}

function firstString(...values: unknown[]): string {
    for (const value of values) {
        if (typeof value === "string" && value.trim().length > 0) return value.trim();
    }
    return "";
}

function firstHref(...values: unknown[]): string {
    for (const value of values) {
        if (typeof value === "string") {
            const trimmed = value.trim();
            if (trimmed.length > 0 && trimmed !== "#") return trimmed;
        }
    }
    return "";
}

function toAbsolutePath(value: string): string {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "#" || /^([a-z][a-z0-9+.-]*:)?\/\//i.test(trimmed)) return trimmed;
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

function resolveEmbeddedSelfToken(hrefValue: string, data: unknown): string | undefined {
    if (!hrefValue.includes("[]")) return undefined;
    const value = toText(getNestedValue(data, ""));
    if (!value) return undefined;
    return hrefValue.replaceAll("[]", encodeURIComponent(value));
}

export function resolveHref(
    hrefValue: unknown,
    data?: unknown,
    context?: RenderContext,
): string | undefined {
    if (typeof hrefValue !== "string" || !hrefValue) return undefined;
    if (!hrefValue.startsWith("[") || !hrefValue.endsWith("]")) {
        const embedded = resolveEmbeddedSelfToken(hrefValue, data);
        return embedded ?? hrefValue;
    }

    const placeholderKey = hrefValue.slice(1, -1);
    const item = data as Record<string, unknown> | undefined;
    if (placeholderKey === "event_url") {
        const slug = firstString(
            (item?.event as Record<string, unknown> | undefined)?.slug,
            item?.slug,
            item?.event_slug,
        );
        if (slug) return `/events/${encodeURIComponent(slug)}`;
        const fallback = firstHref(item?.event_url, item?.url, item?.link);
        return fallback ? toAbsolutePath(fallback) : "/events";
    }

    const directValue = getNestedValue(data, placeholderKey);
    if (isRenderableHref(directValue)) return toAbsolutePath(directValue);

    switch (placeholderKey) {
        case "event_detail_url": {
            const slug = firstString(item?.slug);
            return slug ? `/events/${encodeURIComponent(slug)}` : "/events";
        }

        case "promotion_detail_url": {
            const slug = firstString(item?.slug);
            if (!slug) return "/promotion";
            return `/promotion/${encodeURIComponent(slug)}`;
        }

        case "merchant_detail_url": {
            const slug = firstString(item?.slug);
            if (slug) return `/store/${encodeURIComponent(slug)}`;
            const fallback = firstHref(item?.merchant_detail_url, item?.store_url);
            return fallback ? toAbsolutePath(fallback) : "/all-stores/A";
        }

        case "out_link":
            // Outbound affiliate links are external by design; leave untouched.
            return firstString(item?.out_link, item?.url, item?.link) || undefined;

        case "read_more_url":
        case "redirect_url":
        case "url":
        case "link":
        case "merchant_url":
        case "category_url":
        case "promotion_url":
        case "product_url":
        default: {
            const fallback = firstHref(
                directValue,
                item?.[placeholderKey],
                item?.url,
                item?.link,
                item?.read_more_url,
                item?.redirect_url,
                item?.merchant_url,
                item?.category_url,
                item?.promotion_url,
                item?.event_url,
                item?.product_url,
            );
            void context;
            return fallback ? toAbsolutePath(fallback) : undefined;
        }
    }
}

function normalizeRelativeUrl(value: unknown): string {
    if (typeof value !== "string") return "";
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (/^(https?:\/\/|\/\/|#)/i.test(trimmed)) return trimmed;
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

export function normalizeTemplateItem(item: unknown): unknown {
    if (!item || typeof item !== "object") return item;
    const source = item as Record<string, any>;

    const url = normalizeRelativeUrl(source.url || source.category_url || source.link || source.read_more_url);
    const image = source.image || source.category_image || source.icon || "";

    return {
        ...source,
        name:
            source.name ||
            source.category_name ||
            source.subcategory_name ||
            source.merchant_name ||
            source.offer_title ||
            source.blog_title ||
            "",
        store_name: source.store_name || source.merchant_name || source.name || "",
        merchant_name: source.merchant_name || source.store_name || source.name || "",
        merchant_logo: source.merchant_logo || source.logo || source.image || source.category_image || "",
        merchant_url:
            source.merchant_url ||
            source.url ||
            source.category_url ||
            source.link ||
            source.merchant?.website_url ||
            source.merchant?.url ||
            // Last resort: some merchant-list endpoints (get_top_promotional_merchant)
            // return only a bare domain-style `slug` ("acurite.com"), never a real
            // URL. Routing to "/acurite.com" 404s — this is the store page's own path.
            (source.slug ? `/store/${source.slug}` : "") ||
            "",
        slug: source.slug || source.unique_id || "",
        unique_id: source.unique_id || source.slug || "",
        offer_title: source.offer_title || source.title || source.name || "",
        coupon_code: source.coupon_code || source.code || "",
        product_image: source.product_image || source.image || "",
        blog_title: source.blog_title || source.title || source.name || "",
        category_name: source.category_name || source.name || "",
        subcategory_name: source.subcategory_name || source.name || "",
        category_url: source.category_url || url || "#",
        url: url || source.url || "#",
        link: source.link || url || "#",
        read_more_url: source.read_more_url || url || "#",
        category_image: source.category_image || image,
        image,
        slide_image: source.slide_image || image,
        slide_title:
            source.slide_title ||
            source.text ||
            source.name ||
            source.category_name ||
            source.offer_title ||
            "",
        child: Array.isArray(source.child) ? source.child.map(normalizeTemplateItem) : [],
    };
}


export function convertStylesToInline(styles?: ComponentStyles): CSSProperties {
    if (!styles) return {};
    const inline: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(styles)) {
        if (value === null || value === undefined || typeof value === "object") continue;
        inline[key] = value;
    }
    return inline as CSSProperties;
}

function toCols(span?: number): number {
    if (!span || span <= 0) return 1;
    return Math.max(1, Math.min(12, Math.floor(12 / span)));
}

/** gridCols holds a SPAN per breakpoint; the column count is 12 / span. */
export function getGridStyle(gridCols?: GridSize, gridGap?: number): CSSProperties {
    const columns = toCols(gridCols?.lg || gridCols?.md || gridCols?.sm || gridCols?.xs || 12);
    return {
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap: `${gridGap || 24}px`,
    };
}

export function getItemsLayoutStyle(
    itemsLayout: "grid" | "flex-wrap" | "flex-nowrap" | undefined,
    gridCols?: GridSize,
    gridGap?: number,
): CSSProperties {
    if (itemsLayout === "flex-wrap" || itemsLayout === "flex-nowrap") {
        return {
            display: "flex",
            flexWrap: itemsLayout === "flex-wrap" ? "wrap" : "nowrap",
            alignItems: "center",
            gap: `${gridGap || 24}px`,
        };
    }
    return getGridStyle(gridCols, gridGap);
}

/** Tailwind column classes for a grid, derived from the per-breakpoint span. */
export function getGridClasses(gridCols?: GridSize): string {
    if (!gridCols) return "grid-cols-1";
    const classes: string[] = [];
    if (gridCols.xs) classes.push(`grid-cols-${toCols(gridCols.xs)}`);
    if (gridCols.sm) classes.push(`sm:grid-cols-${toCols(gridCols.sm)}`);
    if (gridCols.md) classes.push(`md:grid-cols-${toCols(gridCols.md)}`);
    if (gridCols.lg) classes.push(`lg:grid-cols-${toCols(gridCols.lg)}`);
    return classes.length > 0 ? classes.join(" ") : "grid-cols-1";
}

/** Grid classes plus a Tailwind gap step (gap-N == N * 4px). */
export function getGridWithGapClasses(gridCols?: GridSize, gridGap?: number): string {
    const gapClass = gridGap ? `gap-${Math.round(gridGap / 4)}` : "gap-6";
    return `${getGridClasses(gridCols)} ${gapClass}`;
}

export function getColumnClasses(size?: GridSize): string {
    if (!size) return "col-span-1";
    const classes: string[] = [];
    if (size.xs) classes.push(`col-span-${size.xs}`);
    if (size.sm) classes.push(`sm:col-span-${size.sm}`);
    if (size.md) classes.push(`md:col-span-${size.md}`);
    if (size.lg) classes.push(`lg:col-span-${size.lg}`);
    return classes.length > 0 ? classes.join(" ") : "col-span-1";
}

/** Columns size against the ROW's `--custom-theme-column-gap`. */
export function getColumnFlexStyle(size?: GridSize): CSSProperties {
    const activeSpan = size?.lg || size?.md || size?.sm || size?.xs;
    if (!activeSpan) return { minWidth: 0, boxSizing: "border-box" };

    const widthPercent = `${(activeSpan / 12) * 100}%`;
    const widthValue =
        activeSpan === 12 ? widthPercent : `calc(${widthPercent} - (var(--custom-theme-column-gap, 0px) / 2))`;

    return { flex: `0 1 ${widthValue}`, maxWidth: widthValue, minWidth: 0, boxSizing: "border-box" };
}


const IMAGE_FALLBACK_KEYS = [
    "image",
    "icon",
    "category_image",
    "category_icon",
    "featured_image",
    "thumbnail",
    "banner_image",
    "merchant_logo",
] as const;

/** CustomTheme prefixes a bare path with "/" rather than an asset host. */
export function normalizeImageSrc(src: unknown): string {
    if (typeof src !== "string") return "";
    const trimmed = src.trim();
    if (!trimmed) return "";
    if (/^(https?:\/\/|\/\/|data:|blob:)/i.test(trimmed)) return trimmed;
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

/** The placeholder-then-nine-key fallback chain ImageComponent uses. */
export function resolveImageSrc(node: { data?: unknown; placeholder?: string }, data: unknown): string {
    const item = data as Record<string, unknown> | undefined;

    const resolved = (() => {
        if (node.placeholder && data) {
            const dynamicValue = getNestedValue(data, node.placeholder);
            const value = typeof dynamicValue === "string" ? dynamicValue.trim() : dynamicValue;
            if (value) return value;
            for (const key of IMAGE_FALLBACK_KEYS) {
                if (item?.[key]) return item[key];
            }
            return node.data;
        }
        if (node.data) return node.data;
        for (const key of IMAGE_FALLBACK_KEYS) {
            if (item?.[key]) return item[key];
        }
        return "";
    })();

    return normalizeImageSrc(resolved);
}


export function resolveRepeatedSource(
    node: { repeated?: boolean; dataSourceKey?: string; offset?: number | "half" | "secondHalf" },
    data: unknown,
): unknown[] | null {
    if (Array.isArray(data)) return applyOffset(data, node.offset);
    if (node.repeated && node.dataSourceKey) {
        const nested = getNestedValue(data, node.dataSourceKey);
        if (Array.isArray(nested)) return applyOffset(nested, node.offset);
    }
    return null;
}

function applyOffset<T>(items: T[], offset: number | "half" | "secondHalf" | undefined): T[] {
    if (!offset) return items;
    // The midpoint is computed from the array's real length — the template
    // cannot hardcode one it never sees. "half" both skips 0 AND stops at the
    // midpoint (a plain skip alone would return the whole rest of the array,
    // duplicating "secondHalf"'s half back into "half"'s).
    if (offset === "half") return items.slice(0, Math.ceil(items.length / 2));
    if (offset === "secondHalf") return items.slice(Math.ceil(items.length / 2));
    return items.slice(offset);
}

export function resolveBoundedRepeatedSource(
    node: { repeated?: boolean; dataSourceKey?: string; offset?: number | "half" | "secondHalf"; totalCards?: number },
    data: unknown,
): unknown[] | null {
    const items = resolveRepeatedSource(node, data);
    if (!items) return items;
    return node.totalCards ? items.slice(0, node.totalCards) : items;
}

/** Does this string carry markup? Used by FAQ_ITEM to pick HTML vs text. */
export function hasHtmlContent(value: string): boolean {
    return /<\/?[a-z][\s\S]*>/i.test(value);
}

/** One crumb in a breadcrumb trail. No href on the last (current) crumb. */
export type BreadcrumbCrumb = { label: string; href?: string };

function toTitleCase(value: string): string {
    return value.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function buildBreadcrumbCrumbs(context?: RenderContextLike): BreadcrumbCrumb[] | null {
    const basePath = context?.basePath;

    // Fallback A: dynamic footer pages (Other/page.tsx) fetch staticPageData which
    // has the human-readable page_name — use it when there is no basePath.
    const staticPageName = (context as { staticPageData?: { page_name?: string } } | undefined)?.staticPageData
        ?.page_name;
    if (!basePath && staticPageName) {
        return [
            { label: "Home", href: "/" },
            { label: toTitleCase(staticPageName) },
        ];
    }

    // Fallback B: derive the last crumb from the URL slug segments when the page
    // carries params but no explicit basePath (some Other-page sub-routes).
    if (!basePath) {
        const slugArr = Array.isArray(context?.params?.slug)
            ? (context.params.slug as unknown[]).filter(
                  (s): s is string => typeof s === "string" && s.length > 0,
              )
            : [];
        if (slugArr.length > 0) {
            return [
                { label: "Home", href: "/" },
                { label: toTitleCase(slugArr[slugArr.length - 1]) },
            ];
        }
        return null;
    }

    const baseSegment = basePath.replace(/^\/+|\/+$/g, "");
    if (!baseSegment) return null;

    let slugSegments = Array.isArray(context?.params?.slug)
        ? (context.params.slug as unknown[]).filter(
              (segment): segment is string => typeof segment === "string" && segment.length > 0,
          )
        : [];

    // Routes with a per-company configurable prefix (e.g. the merchant page's store_slug)
    // derive basePath from params.slug[0] itself, so strip it here to avoid showing it twice.
    if (slugSegments[0] === baseSegment) {
        slugSegments = slugSegments.slice(1);
    }

    const crumbs: BreadcrumbCrumb[] = [{ label: "Home", href: "/" }];
    crumbs.push({ label: toTitleCase(baseSegment), href: slugSegments.length > 0 ? `/${baseSegment}` : undefined });

    slugSegments.forEach((segment, idx) => {
        const isLast = idx === slugSegments.length - 1;
        crumbs.push({
            label: toTitleCase(segment),
            href: isLast ? undefined : `/${baseSegment}/${slugSegments.slice(0, idx + 1).join("/")}`,
        });
    });

    return crumbs;
}

/** The subset of RenderContext buildBreadcrumbCrumbs actually reads. */
type RenderContextLike = {
    basePath?: string;
    params?: Record<string, unknown>;
    staticPageData?: unknown;
};
